import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Container,
  Alert,
  Skeleton,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { themeColors } from "../../utils/theme";

const Register: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSignUpComplete, setIsSignUpComplete] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const { register, confirmSignUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password || !confirmPassword || !displayName) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    try {
      setError("");
      setLoading(true);
      const result = await register(email, password, displayName);

      if (!result.isSignUpComplete) {
        // If signup is not complete, we need verification
        setIsSignUpComplete(false);
        setRegisteredEmail(email);
      } else {
        // If signup is complete, redirect to home
        navigate("/");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create an account"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode) {
      setError("Please enter the verification code");
      return;
    }

    try {
      setError("");
      setLoading(true);
      await confirmSignUp(registeredEmail, verificationCode);
      navigate("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            {isSignUpComplete ? "Verify Your Email" : "Register"}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          {!isSignUpComplete ? (
            // Registration Form
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="displayName"
                label="Full Name"
                name="displayName"
                autoComplete="name"
                autoFocus
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, bgcolor: themeColors.primary }}
                disabled={loading}
              >
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={60}
                    height={24}
                    sx={{
                      bgcolor: `${themeColors.background}40`,
                      '&::after': {
                        background: `linear-gradient(90deg, transparent, ${themeColors.background}60, transparent)`,
                      },
                    }}
                  />
                ) : (
                  "Register"
                )}
              </Button>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Link
                  to="/login"
                  style={{ textDecoration: "none", color: themeColors.primary }}
                >
                  Already have an account? Log In
                </Link>
              </Box>
            </Box>
          ) : (
            // Verification Form
            <Box
              component="form"
              onSubmit={handleVerificationSubmit}
              sx={{ mt: 1, width: "100%" }}
            >
              <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
                A verification code has been sent to your email. Please check
                your inbox and enter the code below.
              </Alert>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                value={registeredEmail}
                disabled
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="verificationCode"
                label="Verification Code"
                name="verificationCode"
                autoFocus
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, bgcolor: themeColors.primary }}
                disabled={loading}
              >
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={100}
                    height={24}
                    sx={{
                      bgcolor: `${themeColors.background}40`,
                      '&::after': {
                        background: `linear-gradient(90deg, transparent, ${themeColors.background}60, transparent)`,
                      },
                    }}
                  />
                ) : (
                  "Verify Account"
                )}
              </Button>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Link
                  to="/login"
                  style={{ textDecoration: "none", color: themeColors.primary }}
                >
                  Back to Login
                </Link>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
